/**
 * Server-side helpers for Adobe kndctr_* cookies (identity/cluster).
 * ECID decode logic mirrors mi-headless-utils decodeKndctrCookie (protobuf subset).
 */

const ECID_FIELD_NUMBER = 1;

const WIRE_TYPES = Object.freeze({
  VARINT: 0,
  I64: 1,
  LEN: 2,
  SGROUP: 3,
  EGROUP: 4,
  I32: 5,
});

const decodeVarint = (buffer: Uint8Array, offset: number) => {
  let value = 0;
  let length = 0;
  let byte: number;
  do {
    if (offset < 0 || offset + length >= buffer.length) {
      throw new Error('Invalid varint: buffer ended unexpectedly');
    }
    byte = buffer[offset + length];
    value |= (byte & 0b01111111) << (7 * length);
    length += 1;
    if (length > 10) {
      throw new Error('Invalid varint: too long');
    }
  } while (byte & 0b10000000);
  return { value, length };
};

const base64ToBytes = (base64: string) => {
  const binString = Buffer.from(base64, 'base64').toString('binary');
  return Uint8Array.from(binString, m => m.codePointAt(0)!);
};

const decodeKndctrProtobuf = (buffer: Uint8Array): string => {
  let offset = 0;
  while (offset < buffer.length) {
    const { value: tag, length: tagLength } = decodeVarint(buffer, offset);
    offset += tagLength;
    const wireType = tag & 0b111;
    const fieldNumber = tag >> 3;

    if (fieldNumber === ECID_FIELD_NUMBER && wireType === WIRE_TYPES.LEN) {
      const fieldValueLength = decodeVarint(buffer, offset);
      offset += fieldValueLength.length;
      const ecid = new TextDecoder().decode(buffer.slice(offset, offset + fieldValueLength.value));
      return ecid;
    }

    switch (wireType) {
      case WIRE_TYPES.VARINT:
        offset += decodeVarint(buffer, offset).length;
        break;
      case WIRE_TYPES.I64:
        offset += 8;
        break;
      case WIRE_TYPES.LEN: {
        const fieldValueLength = decodeVarint(buffer, offset);
        offset += fieldValueLength.length + fieldValueLength.value;
        break;
      }
      case WIRE_TYPES.I32:
        offset += 4;
        break;
      case WIRE_TYPES.SGROUP:
      case WIRE_TYPES.EGROUP:
        break;
      default:
        throw new Error(`Malformed kndctr cookie. Unknown wire type: ${wireType}`);
    }
  }

  throw new Error('No ECID found in cookie.');
};

export const normalizeKndctrCookieValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const decodeEcidFromKndctrIdentityCookie = (cookieValue: string): string | null => {
  try {
    const normalized = normalizeKndctrCookieValue(cookieValue);
    const decodedCookie = normalized.replace(/_/g, '/').replace(/-/g, '+');
    const cookieBytes = base64ToBytes(decodedCookie);
    return decodeKndctrProtobuf(cookieBytes);
  } catch {
    return null;
  }
};

export const getAdobeOrgId = (): string =>
  process.env['TARGET_ORG_ID'] ?? process.env['NEXT_PUBLIC_TARGET_ORG_ID'] ?? '664516D751E565010A490D4C@AdobeOrg';

export const getKndctrCookieKey = (suffix: 'identity' | 'cluster'): string => {
  const orgKey = getAdobeOrgId().replace('@', '_');
  return `kndctr_${orgKey}_${suffix}`;
};

export const findCookieCaseInsensitive = (cookies: Record<string, string>, key: string): string | undefined => {
  const target = key.toLowerCase();
  const entry = Object.entries(cookies).find(([cookieKey]) => cookieKey.toLowerCase() === target);
  return entry?.[1];
};

export const buildKndctrStateEntries = (
  browserCookies: Record<string, string>
): Array<{ key: string; value: string; maxAge: number }> => {
  const entries: Array<{ key: string; value: string; maxAge: number }> = [];

  for (const suffix of ['identity', 'cluster'] as const) {
    const key = getKndctrCookieKey(suffix);
    const rawValue = findCookieCaseInsensitive(browserCookies, key);
    if (rawValue) {
      entries.push({
        key,
        value: normalizeKndctrCookieValue(rawValue),
        maxAge: 0,
      });
    }
  }

  return entries;
};